/**
 * 🍖 Health and food — **T129, D64, D79**.
 *
 * This is the whole mechanism, and it is **thirty lines of arithmetic with no
 * database, no socket and no clock of its own**. Everything that decides how a
 * player's two bars move is here, as pure functions of *what they were* and
 * *how much time went by*. The server owns the numbers and the client draws
 * them; both call the same functions in this file, which is what stops the bar
 * on screen from disagreeing with the bar in the database.
 *
 * ## 🎯 The rules, in haugur's own words
 *
 * Asked directly on **2026-08-20** what makes you hungry — the question
 * [ADR-0016](../../../Project/adr/0016-cold-is-charged-to-the-food-meter.md)
 * left open since 12 August:
 *
 * > *"Time makes you hungry, loosing health (like from cold) forces you to burn
 * > food to rebuild health."*
 *
 * So there are exactly **three rules**, and every one of them is his sentence:
 *
 * 1. **Time takes food.** Steadily, whether you are building or standing still.
 *    100 → 0 in three game days, which is one real hour of play.
 * 2. **Health is bought with food.** While you are hurt and there is food left,
 *    health climbs and the food bar pays for it, **one for one**.
 * 3. **No food, and health falls** — down to a floor, and **the floor is why
 *    nothing can kill you yet.**
 *
 * 🔴 **Rule 2 is the important one and it is not decoration.**
 * `Development/Minecraft/05_Survival_Systems.md` §9.6: *"If exactly one thing
 * from this document gets built, this one."* Without it food is a meter that
 * scolds you; with it food is **what you spend to survive a bad day**, which is
 * what makes carrying a stack of dried fish a decision rather than a chore.
 *
 * ## 🔴 Why nothing here can kill you
 *
 * `starvationFloorPercent` is **1**, so hunger drains health to 1 % and stops.
 * 🎯 **haugur chose that on 2026-08-20**, told plainly what the alternative was.
 * **D21 takes everything you carry when you die**, the pack is 34 slots, and
 * there is nowhere in the world yet that death cannot reach — §9.3 names
 * *frequent death plus total loss* as the incoherent combination, and this is
 * exactly that corner. ⚠️ The floor is a **number in `tuning.json`**, not a
 * branch in the code: when the hearth exists, death is switched on by editing a
 * data file, and nothing in this module changes.
 *
 * ⚠️ It is also Minecraft's own answer rather than an invention — starvation
 * stops at 1 HP on Normal and at 10 HP on Easy (§3.1). The pattern *soften the
 * floor, never delete the mechanic* is marked load-bearing there.
 *
 * ## ⚠️ Every rate is on the GAME clock, and the caller passes REAL time
 *
 * The world runs at 72× (**D41**), so the two are ambiguous by that factor and
 * the tuning fields say which they are in their names. This module takes
 * **real** milliseconds — because that is what a `setInterval` and a frame loop
 * actually measure — and converts once, through `gameHoursToRealMs`, which is
 * the single place in the project that knows the ratio.
 */

import { TUNING, type Tuning, gameDaysToRealMs, gameHoursToRealMs } from './tuning.ts';
import type { EdibleDef } from './items.ts';

/**
 * A player's two bars. **Percentages, 0–100**, not hearts and not drumsticks.
 *
 * 🎯 **haugur, on the health bar:** *"a scale from zero to one hundred, similar
 * to Minecraft's hearts but with a cooler design to come later."* Minecraft's
 * 20 HP is an integer count precisely so hearts can be drawn half-full; ours is
 * a bar, so a percentage is the honest representation and there is no rounding
 * step where a value can be lost.
 */
export interface Vitals {
  /** 0–100. 100 is unhurt. */
  readonly healthPercent: number;
  /** 0–100. 100 is not hungry at all. */
  readonly foodPercent: number;
}

/** A new player, or one who has just woken up: both bars full. */
export const FULL_VITALS: Vitals = { healthPercent: 100, foodPercent: 100 };

/**
 * Why the bars are doing what they are doing — a **key**, never a sentence.
 *
 * 🔴 **D64 §7: the HUD says WHY, in words.** *"Exposed — wind, no shelter."*
 * And §18 of the gameplay notes says why it matters here rather than later:
 * **with one number for health you cannot tell starving from injured.** A fall,
 * a creature and an empty stomach all move the same bar, and Minecraft keeps
 * them apart with a separate hunger meter so that you can tell. We have the
 * separate meter *and* we name the reason, which costs nothing.
 *
 * ⚠️ **A key rather than English**, for the same reason `edit_rejected` carries
 * one (protocol 8, T124): the server has no idea what language this player
 * reads and must never have to know.
 *
 * | Key | What it means |
 * |---|---|
 * | `null` | Fed, unhurt, nothing to say |
 * | `hungry` | Food is low and falling; nothing has hurt you yet |
 * | `starving` | Food is **gone** and it is taking your health |
 * | `recovering` | Health is climbing, and food is paying for it |
 */
export type VitalsReasonKey = 'hungry' | 'starving' | 'recovering' | null;

/**
 * Below this the food bar is worth mentioning.
 *
 * ⚠️ **A warning threshold, not a mechanic** — nothing behaves differently at
 * 30 %, the screen merely starts saying *hungry*. It lives here rather than in
 * `tuning.json` deliberately: `tuning.json` is for numbers that change how the
 * game *plays*, and a number that only decides when a word appears would be
 * noise in a file haugur is meant to be able to read (**D69**).
 */
export const HUNGRY_BELOW_PERCENT = 30;

/**
 * The longest slice of time the arithmetic is allowed to take in one go.
 *
 * ⚠️ **The step is piecewise, not linear**, which is the whole reason this
 * exists. Food falling to zero *changes what happens next* — healing stops and
 * starvation begins — so a single multiplication over a long gap would apply
 * the wrong rule to the second half of it. Slicing keeps every slice inside one
 * regime.
 *
 * A tenth of a game hour is five real seconds, so an ordinary tick (the server
 * corrects every 30 s, the client redraws every frame) is at most a handful of
 * slices.
 */
const SLICE_GAME_HOURS = 0.1;

/**
 * ⚠️ **And a hard ceiling on how many slices**, so that a wild `elapsed` — a
 * laptop lid closed for a day, a clock that jumped — cannot spin the loop. Past
 * this the slices simply get longer, which loses a little precision on a gap
 * nobody was watching anyway. **A loop whose length comes from outside is a
 * loop that stops the server** (standards §3).
 */
const MAX_SLICES = 512;

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

/**
 * Move the bars forward by `elapsedRealMs`.
 *
 * 🔴 **Pure, and it is the only thing that changes a player's vitals.** The
 * server calls it on its save interval and the client calls it every frame
 * between corrections — the same code, so the bar that is drawn is the bar that
 * is stored, and a discrepancy between them is a bug in one caller rather than
 * a difference of opinion between two implementations.
 *
 * ⚠️ **A negative or absurd `elapsed` returns the bars unchanged.** Time going
 * backwards means a clock was corrected, not that a player got younger.
 */
/**
 * The optional half of {@link stepVitals}, **named rather than positional**.
 *
 * 🔴 **This is a bag and not two arguments because of a trap this project has
 * already been bitten by.** `stepVitals` used to take `tuning` third. T131
 needed to add an exposure multiplier, and the obvious place for it was — third.
 * **Every existing call would have kept compiling in a language with looser
 * types, and silently passed a tuning object where a multiplier was expected.**
 * ⚠️ `tsc` did catch it here, and that is luck rather than design: the same
 * shape is exactly how `edible.restoresPercent` changed meaning without
 * changing (`06_Traps.md`). **A name cannot be passed by accident.**
 */
export interface StepOptions {
  /**
   * 🥶 What exposure multiplies the food drain by — **1 is comfortable** (T131).
   *
   * 🔴 **A multiplier on rule ① below, and NOT a fourth rule.** That is
   * ADR-0016 §2 exactly: exposure is *a condition, like standing in fire*. It
   * has no meter, no bar and no screen of its own; it makes the clock hungrier.
   *
   * ⚠️ **It defaults to 1 so that a caller who has no weather yet gets the
   * ordinary drain rather than an error** — a new connection, a flat debug
   * session, or the moment before the first `weather` message lands.
   * ⚠️ **Anything below 1 is refused**: exposure can only ever cost you, and a
   * negative multiplier would let bad weather FEED you, which would be a very
   * funny bug to find six weeks late.
   */
  readonly exposure?: number;
  /** The balance numbers. Defaults to the ones in `tuning.json`. */
  readonly tuning?: Tuning;
}

export function stepVitals(from: Vitals, elapsedRealMs: number, opts: StepOptions = {}): Vitals {
  const tuning = opts.tuning ?? TUNING;
  if (!Number.isFinite(elapsedRealMs) || elapsedRealMs <= 0) return from;

  const exposure =
    opts.exposure !== undefined && Number.isFinite(opts.exposure) ? Math.max(1, opts.exposure) : 1;

  const sliceMs = Math.max(gameHoursToRealMs(SLICE_GAME_HOURS), elapsedRealMs / MAX_SLICES);

  let health = from.healthPercent;
  let food = from.foodPercent;
  let left = elapsedRealMs;

  while (left > 0) {
    const step = Math.min(sliceMs, left);
    left -= step;

    /* ① Time takes food. His first sentence, and it is unconditional: it costs
       the same to build a wall as to stand and look at one. That is deliberate
       — a meter that punishes work is the chore D24 says this family would
       hate, and it is why Minecraft's activity-driven exhaustion was NOT
       copied.

       🥶 And this is the ONE line exposure touches (T131). Standing in a storm
       does not slow you, hurt you or take the controls — it makes this
       subtraction bigger. **You can always walk out into the storm; you just
       have to have eaten.** */
    food = clamp(food - hungerPerMs(tuning) * exposure * step, 0, tuning.health.maxPercent);

    if (health < tuning.health.maxPercent && food > 0) {
      /* ② Health is bought with food, one point for one point. Only as much as
         there is food to pay for — so a nearly empty bar buys a little healing
         and then stops, rather than going into debt. */
      const wanted = (tuning.health.regenHealthPercentPerGameHour / gameHoursToRealMs(1)) * step;
      const room = tuning.health.maxPercent - health;
      const affordable = food / tuning.health.foodPercentPerHealthPercent;
      const healed = Math.min(wanted, room, affordable);
      health += healed;
      food = clamp(
        food - healed * tuning.health.foodPercentPerHealthPercent,
        0,
        tuning.health.maxPercent,
      );
    } else if (food <= 0) {
      /* ③ No food, and health falls — to the floor in `tuning.json` and no
         further. 🔴 The floor is 1 today, so this cannot kill anybody. */
      const rate = tuning.health.starveHealthPercentPerGameHour / gameHoursToRealMs(1);
      health = Math.max(tuning.health.starvationFloorPercent, health - rate * step);
    }
  }

  return {
    healthPercent: clamp(health, 0, tuning.health.maxPercent),
    foodPercent: clamp(food, 0, tuning.health.maxPercent),
  };
}

/** How much food one real millisecond of simply existing costs. */
function hungerPerMs(tuning: Tuning): number {
  return tuning.health.maxPercent / gameDaysToRealMs(tuning.health.hungerZeroGameDays);
}

/**
 * What the screen should say, or `null` for *nothing worth saying*.
 *
 * ⚠️ **Starving outranks recovering**, because they can both be true in the
 * same instant — food hits zero on the tick that health was climbing — and the
 * player needs to be told the thing they can act on.
 */
export function vitalsReason(vitals: Vitals, tuning: Tuning = TUNING): VitalsReasonKey {
  if (vitals.foodPercent <= 0) return 'starving';
  if (vitals.healthPercent < tuning.health.maxPercent) return 'recovering';
  if (vitals.foodPercent < HUNGRY_BELOW_PERCENT) return 'hungry';
  return null;
}

/**
 * The bars after eating one of something — **or the same bars back, unchanged.**
 *
 * 🔴 **Eating fills the FOOD bar, not health**, and that is a correction this
 * task had to make rather than a decision it invented. haugur's original notes
 * predate the second bar and say *"eating one berry block restores ten percent
 * health"*; **D79 then gave him the two bars he asked for** — *"1 food bar 1
 * health bar"* — and on 2026-08-20 he described the link between them himself:
 * *"burn food to rebuild health."* With two bars, food is what a berry fills
 * and health is what food buys. The berry's own numbers did not change at all.
 *
 * ✅ **And the raw-versus-cooked reward survives the move intact.** `ceiling`
 * is why: raw meat stops at 50 % and cooked goes to 100 %, which read against
 * the food bar means exactly what haugur meant against the health bar — *"this
 * rewards the player for working with the meat"* (**D71**).
 *
 * 🔴 **Returning the same bars is a real outcome, not a failure**, and the
 * caller must say so on screen (**D64 §7**). It happens whenever you are
 * already at or above what this food can take you to: full on berries, or at
 * 60 % holding raw meat that stops at 50.
 */
export function vitalsAfterEating(vitals: Vitals, food: EdibleDef): Vitals {
  if (vitals.foodPercent >= food.ceilingPercent) return vitals;
  return {
    ...vitals,
    foodPercent: Math.min(food.ceilingPercent, vitals.foodPercent + food.restoresPercent),
  };
}
